import React, {FC, MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {DataGrid, GridColumns, GridRowId, GridSortModel} from '@mui/x-data-grid';
import {Box, LinearProgress, tableClasses} from '@mui/material';
import {
    CombinedEquipmentRequestEntity,
    EquipmentRequestEntity,
    EquipmentRequestStatusEnum,
    useUpdateEquipmentRequestsStatusesMutation
} from 'graphql/graphQlApiHooks';
import {useEditRequestAction} from "../../hooks/actions/useEditRequestAction";
import {useDeleteRequestAction} from "../../hooks/actions/useDeleteRequestAction";
import {useViewRequestAction} from "../../hooks/actions/useViewRequestAction";
import {styled} from "@mui/styles";
import {darken} from "@mui/material/styles";
import {Footer} from "./components/Footer";
import {useCreateCombinedEquipmentRequestAction} from "../../hooks/actions/useCreateCombinedEquipmentRequestAction";
import {useViewCombineRequest} from "../../hooks/actions/useViewCombineRequest";
import {useEditCombineRequest} from "../../hooks/actions/useEditCombineRequest";
import {useDeleteCombineRequest} from "../../hooks/actions/useDeleteCombineRequest";
import {RequestEntity} from "../../types/RequestEntity";
import {isCombinedRequest} from "../../lib/isCombinedRequest";
import {isSingleRequest} from "../../lib/isSingleRequest";
import {columns} from "./columns";
import {useMultiplySuccessRequests} from "../../hooks/actions/useMultiplySuccessRequests";
import {useAddCombinedEquipmentRequestAction} from '../../hooks/actions/useAddCombinedEquipmentRequestAction';
import {makeVar, useReactiveVar} from '@apollo/client';
import {BulkStatusChangeDialog} from '../Dialogs/BulkStatusChangeDialog';
import {BulkEquipmentAcceptanceDialog} from '../Dialogs/BulkEquipmentAcceptanceDialog';
import {useDialog} from 'providers/DialogProvider';

export const combinedRequestsStateVar = makeVar<{ [key: string]: boolean }>({});


type Props = {
    rows: RequestEntity[];
    loading: boolean;
    allCount: number;
    count: number;
    selected: GridRowId[];
    handleSelectionChange: (newSelection: GridRowId[]) => void;
    contextMenuHandler: (event: MouseEvent) => void;
    contextMenuCloseHandler: () => void;
    handleSortModelChange: (model: GridSortModel) => void;
    handlePageChange: (page: number) => void;
    handlePageSizeChange: (pageSize: number) => void;
    refetchData: () => void;
    updateRequestsStatuses: (updatedRequests: any[]) => Promise<void>;
    contextRow: RequestEntity | null;
    contextMenu: { mouseX: number; mouseY: number } | null;
    pageSize: number;
    page: number;
    combinedRequestsLength: number
}

export const RequestTable: FC<Props> = (props) => {
    const {
        rows,
        loading,
        allCount,
        count,
        handleSelectionChange,
        contextMenuHandler,
        contextMenuCloseHandler,
        handleSortModelChange,
        handlePageChange,
        handlePageSizeChange,
        contextRow,
        contextMenu,
        pageSize,
        page,
        combinedRequestsLength,
        refetchData,
        updateRequestsStatuses
    } = props;
    const combinedRequestsState = useReactiveVar(combinedRequestsStateVar);

    const {onCreateCombinedEquipmentRequestAction} = useCreateCombinedEquipmentRequestAction()
    const {onAddToCombinedEquipmentRequestAction} = useAddCombinedEquipmentRequestAction()
    const [groupedRequests, setGroupedRequests] = useState<RequestEntity[]>([]);
    const [selected, setSelected] = useState<GridRowId[]>([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState<EquipmentRequestStatusEnum | null>(null);
    const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
    const [dialog, closeDialog] = useDialog();

    const handleCheckboxChange = useCallback((id: GridRowId) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelected((prevSelected) =>
            event.target.checked
                ? [...prevSelected, id]
                : prevSelected.filter((selectedId) => selectedId !== id)
        );
    }, []);

    const {handleEdit} = useEditRequestAction();
    const {handleEditCombineRequest} = useEditCombineRequest()
    const {handleDelete} = useDeleteRequestAction()
    const {handleDeleteCombineRequest} = useDeleteCombineRequest()
    const {handleView} = useViewRequestAction()
    const {handleViewCombineRequest} = useViewCombineRequest()
    const {multiplySuccessRequests} = useMultiplySuccessRequests()
    
    const [updateEquipmentRequestsStatuses] = useUpdateEquipmentRequestsStatusesMutation();

    const openBulkEquipmentAcceptanceDialog = () => {
        const selectedRequestIds = selected.map(id => String(id));
        const selectedRequests = rows.filter(row => selectedRequestIds.includes(row.id) && isSingleRequest(row)) as EquipmentRequestEntity[];
        
        dialog({
            onClose: closeDialog,
            title: "",
            maxWidth: "md",
            fullWidth: true,
            children: (
                <BulkEquipmentAcceptanceDialog
                    equipmentRequests={selectedRequests}
                    onClose={closeDialog}
                    loading={bulkStatusLoading}
                    onAccept={(acceptanceCertificateId, acceptedEquipmentRequests) => {
                        executeBulkEquipmentAcceptance(acceptanceCertificateId, acceptedEquipmentRequests);
                    }}
                />
            )
        });
    };

    const executeBulkEquipmentAcceptance = async (acceptanceCertificateId: string, acceptedEquipmentRequests: Array<{equipmentRequestId: string, quantity: number, name?: string}>) => {
        try {
            setBulkStatusLoading(true);
            
            const { data } = await updateEquipmentRequestsStatuses({
                variables: {
                    input: {
                        requestIds: acceptedEquipmentRequests.map(req => req.equipmentRequestId),
                        newStatus: EquipmentRequestStatusEnum.EquipmentAccepted,
                        acceptanceCertificateId,
                        acceptedEquipmentRequests
                    }
                }
            });
            
            if (data?.equipmentRequest_updateEquipmentRequestsStatuses) {
                await updateRequestsStatuses(data.equipmentRequest_updateEquipmentRequestsStatuses);
                refetchData();
                setSelected([]);
                closeDialog();
            }
        } catch (error) {
            console.error('Ошибка при массовом принятии оборудования:', error);
        } finally {
            setBulkStatusLoading(false);
        }
    };

    const handleBulkStatusChange = (newStatus: EquipmentRequestStatusEnum) => {
        if (newStatus === EquipmentRequestStatusEnum.EquipmentAccepted) {
            openBulkEquipmentAcceptanceDialog();
            return;
        }
        
        setPendingStatusChange(newStatus);
        setConfirmDialogOpen(true);
    };

    const executeBulkStatusChange = async (comment: string) => {
        if (!pendingStatusChange) return;
        
        try {
            setBulkStatusLoading(true);
            
            const selectedRequestIds = selected.map(id => String(id));
            
            const { data } = await updateEquipmentRequestsStatuses({
                variables: {
                    input: {
                        requestIds: selectedRequestIds,
                        newStatus: pendingStatusChange,
                        rejectReason: comment || undefined
                    }
                }
            });
            
            if (data?.equipmentRequest_updateEquipmentRequestsStatuses) {
                
                await updateRequestsStatuses(data.equipmentRequest_updateEquipmentRequestsStatuses);
                
                setSelected([]);
                setConfirmDialogOpen(false);
                setPendingStatusChange(null);
                
            }
        } catch (error) {
            setConfirmDialogOpen(false);
            setPendingStatusChange(null);
        } finally {
            setBulkStatusLoading(false);
        }
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
        setPendingStatusChange(null);
    };

    const toggleGroup = useCallback((groupId: string) => {
        combinedRequestsStateVar({
            ...combinedRequestsState,
            [groupId]: !combinedRequestsState[groupId],
        });
    }, [combinedRequestsState]);

    useEffect(() => {
        setGroupedRequests(
            (rows || [])
                .map((row) => {
                        if (isCombinedRequest(row)) {
                            const isOpen = combinedRequestsState[row.id] || false;
                            return {...row, isOpen};
                        }
                        return row
                    }
                )
        )
    }, [rows, combinedRequestsState]);



    const rowsData = groupedRequests.flatMap((group) => {
            if (isCombinedRequest(group) && group?.childRequests && group?.childRequests?.length > 0) {
                return [
                    group,
                    ...((group.isOpen) ? group?.childRequests.map((req, index) => ({
                        ...req,
                        isInCombinedRequest: true,
                        indexNumber: index + 1
                    })) : []),
                ];
            }

            return group;
        }
    );

    const equipmentsRequestColumns: GridColumns<RequestEntity> = useMemo(() => columns({
        toggleGroup,
        selected,
        handleCheckboxChange,
        rowsData: rowsData as CombinedEquipmentRequestEntity[]
    }), [toggleGroup, selected, handleCheckboxChange, rowsData]);

    const getRowClassName = useCallback((params) => {
        const isGroupRow = isCombinedRequest(params.row);
        const isChildRow = params.row.isInCombinedRequest;
        let classes = '';

        if (isGroupRow && params.row.isOpen) {
            classes = 'group-row';
        }

        if (isChildRow) {
            classes = 'child-row';
        }

        if (params.row.id === contextRow?.id && contextMenu !== null) {
            classes += ' isOpenContextMenu';
        }

        return classes;
    }, [contextRow?.id, contextMenu]);

    const handleRowDoubleClick = useCallback((params: any) => {
        isSingleRequest(params.row) ? handleView(params.row as EquipmentRequestEntity) : handleViewCombineRequest(params.row as CombinedEquipmentRequestEntity);
    }, [handleView, handleViewCombineRequest]);

    return (
        <Box sx={{height: '100%', width: '100%'}}>
            <StyledDataGrid
                rows={rowsData}
                columns={equipmentsRequestColumns}
                getRowId={(row) => row.id}
                getRowHeight={() => 50}
                getRowClassName={getRowClassName}
                // className={tableClasses.root}
                disableSelectionOnClick
                disableExtendRowFullWidth
                scrollbarSize={8}
                columnBuffer={3}
                columnThreshold={3}
                rowBuffer={5}
                rowThreshold={5}
                loading={loading || !rows}
                onRowDoubleClick={handleRowDoubleClick}
                sx={{
                    "& .isOpenContextMenu": {
                        bgcolor: "action.hover"
                    }
                }}
                components={{
                    Footer: Footer,
                    LoadingOverlay: LinearProgress,
                    NoResultsOverlay: () => {
                        return <Box sx={{
                            display: "flex",
                            bgcolor: "background.default",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%"
                        }}/>;
                    },
                    NoRowsOverlay: () => {
                        return <Box sx={{
                            display: "flex",
                            bgcolor: "background.default",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%"
                        }}/>;
                    }

                }}
                componentsProps={{
                    footer: {
                        selected,
                        combinedRequestsLength: combinedRequestsLength,
                        selectedAvailable: selected.map((id) => {
                            const row = rows.find((row) => row.id === id) as EquipmentRequestEntity;
                            if (row?.requestStatus === EquipmentRequestStatusEnum.Submitted) {
                                return row.id
                            } else {
                                return null
                            }
                        }).filter((id) => id !== null).length,
                        onCreateCombinedEquipmentRequest: () => {
                            onCreateCombinedEquipmentRequestAction({
                                equipments: rows.filter(row => selected.includes(row.id)) as EquipmentRequestEntity[],
                                onSuccess: () => {
                                    setSelected([])
                                }
                            })
                        },
                        onAddToCombinedEquipmentRequest: () => {
                            onAddToCombinedEquipmentRequestAction({
                                equipments: rows.filter(row => selected.includes(row.id)) as EquipmentRequestEntity[],
                                onSuccess: () => {
                                    setSelected([])
                                },
                                open: true
                            })
                        },
                        onTakeToWorkHandler: () => multiplySuccessRequests({
                            ids: selected.map((id) => {
                                const row = rows.find((row) => row.id === id) as EquipmentRequestEntity;
                                if (row?.requestStatus === EquipmentRequestStatusEnum.Submitted) {
                                    return row.id
                                } else {
                                    return null
                                }
                            }).filter((id) => id !== null),
                            onSuccess: () => {
                                setSelected([])
                            },
                        }),
                        isDisabled: selected.map((id) => {
                            const row = rows.find((row) => row.id === id) as EquipmentRequestEntity;
                            if (row?.requestStatus === EquipmentRequestStatusEnum.Submitted) {
                                return row.id
                            } else {
                                return null
                            }
                        }).filter((id) => id !== null).length === 0,
                        selectedRows: selected.map(id => rows.find(row => row.id === id)).filter(Boolean),
                        onBulkStatusChange: handleBulkStatusChange,
                    },
                    row: {
                        onContextMenu: contextMenuHandler,
                        style: {cursor: "context-menu"}
                    },
                }}
                selectionModel={selected}
                sortingMode='server'
                onSortModelChange={handleSortModelChange}
                paginationMode='server'
                rowCount={count}
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageOptions={[5, 10, 50, 100]}
            />
            {/*<Menu*/}
            {/*    open={contextMenu !== null}*/}
            {/*    onClose={contextMenuCloseHandler}*/}
            {/*    anchorReference='anchorPosition'*/}
            {/*    anchorPosition={*/}
            {/*        contextMenu !== null*/}
            {/*            ? {top: contextMenu.mouseY, left: contextMenu.mouseX}*/}
            {/*            : undefined*/}
            {/*    }*/}
            {/*    elevation={22}*/}
            {/*    sx={{*/}
            {/*        maxWidth: 300,*/}
            {/*        "& .MuiListItemText-root .MuiTypography-root": {*/}
            {/*            textOverflow: "ellipsis",*/}
            {/*            overflow: "hidden",*/}
            {/*            width: "100%",*/}
            {/*            display: "flow-root"*/}
            {/*        }*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <MenuItem*/}
            {/*        onClick={() => {*/}
            {/*        }}*/}

            {/*    >*/}
            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"mdi:show-outline"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Просмотр</ListItemText>*/}
            {/*    </MenuItem>*/}
            {/*    <MenuItem*/}
            {/*        onClick={() => {*/}
            {/*            isSingleRequest(contextRow) && handleView(contextRow as EquipmentRequestEntity);*/}
            {/*            isCombinedRequest(contextRow) && handleViewCombineRequest(contextRow as CombinedEquipmentRequestEntity);*/}
            {/*        }}*/}

            {/*    >*/}
            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"material-symbols:edit-outline"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Изменить</ListItemText>*/}
            {/*    </MenuItem>*/}
            {/*    <MenuItem*/}
            {/*        onClick={() => {*/}
            {/*        }}*/}

            {/*    >*/}
            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"material-symbols:delete-outline-sharp"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Удалить</ListItemText>*/}
            {/*    </MenuItem>*/}
            {/*    <Divider/>*/}
            {/*    <MenuItem*/}
            {/*        dense*/}
            {/*        onClick={() => {*/}
            {/*        }}*/}
            {/*    >*/}

            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"tabler:status-change"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Переместить</ListItemText>*/}
            {/*    </MenuItem>*/}
            {/*    <MenuItem*/}
            {/*        dense*/}
            {/*        onClick={() => {*/}
            {/*        }}*/}
            {/*    >*/}

            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"carbon:category"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Изменить категорию</ListItemText>*/}
            {/*    </MenuItem>*/}
            {/*    <MenuItem*/}
            {/*        dense*/}
            {/*        onClick={() => {*/}
            {/*        }}*/}
            {/*    >*/}

            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"carbon:category"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Убрать из группы заявок</ListItemText>*/}
            {/*    </MenuItem>*/}
            {/*    <MenuItem*/}
            {/*        dense*/}
            {/*        onClick={() => {*/}
            {/*        }}*/}
            {/*    >*/}
            {/*        <ListItemIcon>*/}
            {/*            <Iconify icon={"carbon:category"}/>*/}
            {/*        </ListItemIcon>*/}
            {/*        <ListItemText>Добавить заявку в группу</ListItemText>*/}
            {/*    </MenuItem>*/}

            {/*    <Divider/>*/}
            {/*</Menu>*/}

            <BulkStatusChangeDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={executeBulkStatusChange}
                newStatus={pendingStatusChange}
                selectedCount={selected.length}
                loading={bulkStatusLoading}
            />
        </Box>
    );
};


// const StyledDataGrid = styled(DataGrid)(({theme}) => ({
//     '& .group-row': {
//         backgroundColor: darken(theme.palette.background.paper, .01),
//     },
//     '& .child-row': {
//         backgroundColor: darken(theme.palette.background.paper, .02),
//     },
//     '& .MuiDataGrid-row': {
//         position: 'relative',
//     },
//     '& .MuiDataGrid-cell': {
//         position: 'relative',
//     },
//     '& .row-actions': {
//         visibility: 'hidden',
//         position: 'absolute',
//         right: theme.spacing(2),
//         left: theme.spacing(2),
//         top: '50%',
//         transform: 'translateY(-50%)',
//         width: `calc(100% - ${theme.spacing(4)})`,
//     },
//     '& .MuiDataGrid-row:hover .row-actions': {
//         visibility: 'visible',
//     },
// }));
const StyledDataGrid = DataGrid;

